import http, { authHeaders } from '../../../api/axios';
import { apiUrl } from '../../../api/apiUrl';
import { InvoiceBaseInputs } from '../../../types/Invoice';
import { InvoiceForm } from './InvoiceForm';
import { useCurrentCompany } from '../../../hooks/useCompany';
import { useEffect, useState } from 'react';
import { Loader } from '../../../components/loader/Loader';
import { NumericalSeries } from '../../../types/NumericalSeries';
import { CompanyBankAccount } from '../../../types/CompanyBankAccount';
import { downloadFile } from '../../../utils/functions/file.functions';
import { format, isEqual } from 'date-fns';
import { dateSubmitFormat } from '../../../types/core/date.types';
import { useParams } from 'react-router-dom';
import { Currency } from '../../../types/Currency';
import { useLoading } from '../../../context/loadingContext';
import { useAuth } from '../../../context/AuthContext';

interface Props {
  data: InvoiceBaseInputs;
  onSubmit: (data: InvoiceBaseInputs, hideInfoSuccess?: boolean) => void;
  mutate?: () => void;
}

/**
 * Container for invoice form to fetch shared data
 */
export const InvoiceFormContainer = ({ data, onSubmit, mutate }: Props) => {
  const { user } = useAuth();
  const { company } = useCurrentCompany();
  const { id: invoiceId } = useParams();
  const [documentNumber, setDocumentNumber] = useState<string | undefined>(data.documentNumber);
  const [variableSymbol, setVariableSymbol] = useState<string | undefined>(undefined);
  const [selectedNumericalSeries, setSelectedNumericalSeries] = useState<NumericalSeries | null>(
    null,
  );
  const [numericalSeries, setNumericalSeries] = useState<NumericalSeries[] | null>(null);
  const [companyBankAccounts, setCompanyBankAccounts] = useState<CompanyBankAccount[] | null>(null);
  const [dateOfTaxableTransaction, setDateOfTaxableTransaction] = useState<Date>(
    data.dateOfTaxableTransaction ? new Date(data.dateOfTaxableTransaction) : new Date(),
  );
  const [currenciesToTaxDate, setCurrenciesToTaxDate] = useState<Currency[] | null>(null);
  const [currencyRatesDate, setCurrencyRatesDate] = useState<Date | null>(
    data.currencyRatesDate ?? null,
  );
  const { startLoading, stopLoading } = useLoading();
  /**
   * Loads currencies by date of taxable transaction
   */
  const fetchCurrenciesData = async (byDate?: Date) => {
    const date = byDate ?? dateOfTaxableTransaction;
    const response = await http.apiGet({
      url: `${apiUrl.CURRENCY_GET_LIST}?date=${format(new Date(date), dateSubmitFormat)}`,
    });
    if (response?.data) setCurrenciesToTaxDate(response?.data);
  };
  /**
   * Loads currency date according to model
   */
  const fetchCurrencyRateDate = async () => {
    if (!company) return;
    const response = await http.apiGet({
      url: `${apiUrl.COMPANY_CURRENCY_RATES_DATE_OF_MODEL}?companyId=${company.id}&day=${format(
        dateOfTaxableTransaction,
        dateSubmitFormat,
      )}`,
    });
    if (response?.data) setCurrencyRatesDate(response?.data);
  };

  /**
   * Loads bank account list for current company
   */
  const fetchCompanyBankAccountData = async () => {
    if (!company) return;
    const response = await http.apiGet({
      url: `${apiUrl.COMPANY_BANK_ACCOUNT_LIST}?parentId=${company.id}`,
    });
    setCompanyBankAccounts(response?.data);
  };

  /**
   * Loads numerical series to select invoice document number format
   */
  const fetchNumericalSeriesData = async () => {
    if (!company) return;
    const response = await http.apiGet({
      url: `${apiUrl.NUMERICAL_SERIES_LIST}?parentId=${company.id}`,
    });
    if (
      response?.data.length === 1 &&
      (data.documentNumber === undefined || data.documentNumber === '')
    )
      setSelectedNumericalSeries(response?.data[0]);
    setNumericalSeries(response?.data);
  };

  /**
   * export pdf by id
   */
  const handleExportPdf = async () => {
    startLoading();
    await http
      .apiGet({
        url: `${apiUrl.ACCOUNTING_DOCUMENT_GET_PDF}?id=${invoiceId}&fullName=${user?.fullName}&phoneNumber=${user?.phoneNumber}&codeBookItemsUseCode=${company?.codeBookItemsUseCode}&maxDecimalNumUnitPrice=${company?.maxDecimalNumUnitPrice}`,
      })
      .then(async (res) => {
        if (!res) return;
        await http
          .apiGetFiles({
            url: `${apiUrl.FILE_DOWNLOAD}?id=${res.data}`,
            config: {
              headers: {
                ...authHeaders(),
                'Content-Type': 'application/json',
              },
              method: 'GET',
              responseType: 'blob',
            },
          })
          .then(async (response) => {
            // get filename
            const filename = await response?.headers['content-disposition']
              ?.split('filename=')[1]
              .split(';')[0];
            const fileUrl: string = URL.createObjectURL(response?.data);
            downloadFile(fileUrl, filename ? filename : 'invoice');
            stopLoading();
          });
      });
    stopLoading();
  };

  useEffect(() => {
    if (!dateOfTaxableTransaction) return;
    if (
      !isEqual(new Date(data.dateOfTaxableTransaction ?? new Date()), dateOfTaxableTransaction) ||
      !currencyRatesDate
    )
      fetchCurrencyRateDate();
  }, [dateOfTaxableTransaction, company]);

  useEffect(() => {
    if (!currencyRatesDate) return;
    fetchCurrenciesData(currencyRatesDate);
  }, [currencyRatesDate]);

  useEffect(() => {
    fetchNumericalSeriesData();
    fetchCompanyBankAccountData();
  }, [company]);

  if (
    !company ||
    !numericalSeries ||
    !companyBankAccounts ||
    !data ||
    !currenciesToTaxDate ||
    !currencyRatesDate
  )
    return <Loader />;
  return (
    <InvoiceForm
      data={data}
      onSubmit={onSubmit}
      documentNumber={documentNumber}
      setDocumentNumber={setDocumentNumber}
      variableSymbol={variableSymbol}
      setVariableSymbol={setVariableSymbol}
      numericalSeries={numericalSeries}
      dateOfTaxableTransaction={dateOfTaxableTransaction}
      setDateOfTaxableTransaction={setDateOfTaxableTransaction}
      companyBankAccounts={companyBankAccounts}
      selectedNumericalSeries={selectedNumericalSeries}
      setSelectedNumericalSeries={setSelectedNumericalSeries}
      onExport={invoiceId ? handleExportPdf : undefined}
      currenciesToTaxDate={currenciesToTaxDate}
      currencyRatesDate={currencyRatesDate}
      setCurrencyRatesDate={setCurrencyRatesDate}
      mutate={mutate}
    />
  );
};
